import type { Canvas, FabricImage, StaticCanvas } from "fabric";
import { authedFetch } from "lib/utils/authedFetch";
import {
    computeCanvasContainToFillGridTransform,
    layoutImageContainInGrid,
    type NaturalImageDims,
} from "./mapEditorGrid";

/** Nav2 filter “free” — letterbox areas use white (not black / occupied). */
export const FILTER_MASK_FREE_HEX = "#ffffff";

export type MapEditorSketchBounds = {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
    objectCount: number;
} | null;

/** Axis-aligned bounds of all sketch objects in canvas pixel coordinates. */
export function sketchBoundsFromCanvas(canvas: Canvas): MapEditorSketchBounds {
    const objects = canvas.getObjects();
    if (objects.length === 0) {
        return null;
    }
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const o of objects) {
        const br = o.getBoundingRect();
        minX = Math.min(minX, br.left);
        minY = Math.min(minY, br.top);
        maxX = Math.max(maxX, br.left + br.width);
        maxY = Math.max(maxY, br.top + br.height);
    }
    if (!Number.isFinite(minX)) {
        return null;
    }
    return { minX, minY, maxX, maxY, objectCount: objects.length };
}

/** Copy the annotation Fabric backing store (vector strokes only — never tainted by map images). */
function snapshotAnnotationCanvas(
    annotationCanvas: Canvas,
    width: number,
    height: number,
): HTMLCanvasElement {
    const el = annotationCanvas.lowerCanvasEl ?? annotationCanvas.getElement();
    const out = document.createElement("canvas");
    out.width = width;
    out.height = height;
    const ctx = out.getContext("2d");
    if (!ctx) {
        throw new Error("Could not create export canvas");
    }
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(el, 0, 0, el.width, el.height, 0, 0, width, height);
    return out;
}

/** Download preview URL bytes (for pass-through save when the variant was not edited). */
export async function fetchPreviewImageBlob(url: string): Promise<Blob> {
    const res = await authedFetch(url, { mode: "cors", cache: "no-store" });
    if (!res.ok) {
        throw new Error("Couldn't download the map for export. Try again.");
    }
    return res.blob();
}

async function fetchImageViaBlob(url: string): Promise<HTMLImageElement> {
    const res = await authedFetch(url, { mode: "cors", cache: "no-store" });
    if (!res.ok) {
        throw new Error("Couldn't download the map for export. Try again.");
    }
    const objectUrl = URL.createObjectURL(await res.blob());
    try {
        return await new Promise((resolve, reject) => {
            const im = new window.Image();
            im.onload = () => resolve(im);
            im.onerror = () => reject(new Error("Could not decode image"));
            im.src = objectUrl;
        });
    } finally {
        URL.revokeObjectURL(objectUrl);
    }
}

/**
 * Stretch layout-sized map pixels to the full Nav2 grid (no letterbox white in the saved PNG).
 * Matches {@link computeCanvasContainToFillGridTransform} applied to editor strokes.
 */
function drawLayoutImageFillGrid(
    ctx: CanvasRenderingContext2D,
    img: CanvasImageSource,
    imageNaturalW: number,
    imageNaturalH: number,
    layoutNaturalW: number,
    layoutNaturalH: number,
    gridW: number,
    gridH: number,
): void {
    const safeW = imageNaturalW > 0 ? imageNaturalW : layoutNaturalW;
    const safeH = imageNaturalH > 0 ? imageNaturalH : layoutNaturalH;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(img, 0, 0, safeW, safeH, 0, 0, gridW, gridH);
}

/** Read PNG dimensions from a blob (for save-path debug logging). */
export function loadBlobImageDimensions(blob: Blob): Promise<{ w: number; h: number } | null> {
    return new Promise((resolve) => {
        const url = URL.createObjectURL(blob);
        const im = new window.Image();
        im.onload = () => {
            URL.revokeObjectURL(url);
            const w = im.naturalWidth;
            const h = im.naturalHeight;
            resolve(w > 0 && h > 0 ? { w, h } : null);
        };
        im.onerror = () => {
            URL.revokeObjectURL(url);
            resolve(null);
        };
        im.src = url;
    });
}

/**
 * Stretch preview raster to the full editor grid (1 editor px = 1 Nav2 cell; no letterbox).
 * Use when {@link editorUsesGridFillLayout} is true.
 */
export function applyFabricImageFillGrid(
    img: FabricImage,
    imageNaturalW: number,
    imageNaturalH: number,
    gridW: number,
    gridH: number,
): void {
    const safeW = imageNaturalW > 0 ? imageNaturalW : 1;
    const safeH = imageNaturalH > 0 ? imageNaturalH : 1;
    img.set({
        left: 0,
        top: 0,
        originX: "left",
        originY: "top",
        selectable: false,
        evented: false,
        scaleX: gridW / safeW,
        scaleY: gridH / safeH,
    });
}

/** Letterbox preview inside the grid (legacy; only when live grid metadata is unavailable). */
export function applyFabricImageToLayoutBox(
    img: FabricImage,
    imageNaturalW: number,
    imageNaturalH: number,
    layoutNaturalW: number,
    layoutNaturalH: number,
    gridW: number,
    gridH: number,
): { scale: number; left: number; top: number } {
    const layout = layoutImageContainInGrid(layoutNaturalW, layoutNaturalH, gridW, gridH);
    const boxW = layoutNaturalW * layout.scale;
    const boxH = layoutNaturalH * layout.scale;
    const safeW = imageNaturalW > 0 ? imageNaturalW : 1;
    const safeH = imageNaturalH > 0 ? imageNaturalH : 1;
    img.set({
        left: layout.left,
        top: layout.top,
        originX: "left",
        originY: "top",
        selectable: false,
        evented: false,
        scaleX: boxW / safeW,
        scaleY: boxH / safeH,
    });
    return layout;
}

export type Nav2MaskExportImage = {
    url: string;
    naturalW: number;
    naturalH: number;
};

/**
 * Rasters to composite into the filter mask PNG.
 * Companion BMP is editor background only — when editing a variant, export the variant layer alone.
 */
export function buildMaskExportImages(params: {
    geometryUrl: string | null;
    variantPreviewUrl: string | null;
    layoutNatural: NaturalImageDims;
    companionNatural: NaturalImageDims | null;
    variantNatural: NaturalImageDims | null;
    usesCompanionGeometry: boolean;
}): Nav2MaskExportImage[] {
    const {
        geometryUrl,
        variantPreviewUrl,
        layoutNatural,
        companionNatural,
        variantNatural,
        usesCompanionGeometry,
    } = params;
    if (!geometryUrl || !layoutNatural) {
        return [];
    }
    if (
        usesCompanionGeometry &&
        variantPreviewUrl &&
        variantPreviewUrl !== geometryUrl &&
        variantNatural
    ) {
        return [
            {
                url: variantPreviewUrl,
                naturalW: variantNatural.w,
                naturalH: variantNatural.h,
            },
        ];
    }
    const nat = variantNatural ?? companionNatural ?? layoutNatural;
    return [
        {
            url: geometryUrl,
            naturalW: nat.w,
            naturalH: nat.h,
        },
    ];
}

export type Nav2MaskExportOptions = {
    /** Editor strokes are already 1:1 with grid cells (grid-fill layout, no letterbox transform). */
    strokeGridDirect?: boolean;
};

/**
 * Build Nav2-aligned filter mask PNG. Uses fetch+blob for rasters (avoids tainted canvas).
 * When `strokeGridDirect`, editor and export share the same grid coordinates (no letterbox remap).
 */
export async function exportNav2CellAlignedMask(
    annotationCanvas: Canvas,
    layoutNatural: NaturalImageDims,
    gridW: number,
    gridH: number,
    images: Nav2MaskExportImage[],
    options?: Nav2MaskExportOptions,
): Promise<Blob> {
    if (!layoutNatural) {
        throw new Error("Layout dimensions unknown");
    }
    const { w: layoutW, h: layoutH } = layoutNatural;
    const strokeGridDirect = options?.strokeGridDirect ?? false;
    const letterboxTransform = strokeGridDirect
        ? null
        : computeCanvasContainToFillGridTransform(layoutW, layoutH, gridW, gridH);

    const out = document.createElement("canvas");
    out.width = gridW;
    out.height = gridH;
    const ctx = out.getContext("2d");
    if (!ctx) {
        throw new Error("Could not export image");
    }

    ctx.fillStyle = FILTER_MASK_FREE_HEX;
    ctx.fillRect(0, 0, gridW, gridH);

    for (const spec of images) {
        if (!spec.url) continue;
        const img = await fetchImageViaBlob(spec.url);
        drawLayoutImageFillGrid(
            ctx,
            img,
            spec.naturalW || img.naturalWidth,
            spec.naturalH || img.naturalHeight,
            layoutW,
            layoutH,
            gridW,
            gridH,
        );
    }

    const annEl = snapshotAnnotationCanvas(annotationCanvas, gridW, gridH);
    ctx.imageSmoothingEnabled = false;
    if (letterboxTransform) {
        const { kx, ky, tx, ty } = letterboxTransform;
        ctx.save();
        ctx.setTransform(kx, 0, 0, ky, tx, ty);
        ctx.drawImage(annEl, 0, 0);
        ctx.restore();
    } else {
        ctx.drawImage(annEl, 0, 0);
    }

    const blob = await new Promise<Blob | null>((resolve) =>
        out.toBlob((b) => resolve(b), "image/png"),
    );
    if (!blob) {
        throw new Error("Could not export image");
    }
    return blob;
}

/**
 * Nav2 export from editor strokes + preview URLs (does not snapshot the map Fabric canvas).
 */
export async function exportNav2CellAlignedMaskFromEditor(
    annotationCanvas: Canvas,
    layoutNatural: NaturalImageDims,
    gridW: number,
    gridH: number,
    images: Nav2MaskExportImage[],
    options?: Nav2MaskExportOptions,
): Promise<Blob> {
    return exportNav2CellAlignedMask(
        annotationCanvas,
        layoutNatural,
        gridW,
        gridH,
        images,
        options,
    );
}

/**
 * @deprecated Prefer {@link exportNav2CellAlignedMaskFromEditor} with explicit image list.
 */
export async function exportNav2CellAlignedMaskBlob(
    annotationCanvas: Canvas,
    previewUrl: string,
    layoutNatural: NaturalImageDims,
    gridW: number,
    gridH: number,
    variantPreviewUrl?: string | null,
    previewNatural?: NaturalImageDims,
    variantNatural?: NaturalImageDims,
): Promise<Blob> {
    const images: Nav2MaskExportImage[] = [];
    if (previewUrl) {
        images.push({
            url: previewUrl,
            naturalW: previewNatural?.w ?? 0,
            naturalH: previewNatural?.h ?? 0,
        });
    }
    if (variantPreviewUrl && variantPreviewUrl !== previewUrl) {
        images.push({
            url: variantPreviewUrl,
            naturalW: variantNatural?.w ?? 0,
            naturalH: variantNatural?.h ?? 0,
        });
    }
    return exportNav2CellAlignedMask(annotationCanvas, layoutNatural, gridW, gridH, images);
}

/** Composite base map + annotation layer at fixed grid size (no extra scaling). */
export function mergeMapAndAnnotationsToBlob(
    mapCanvas: StaticCanvas,
    annotationCanvas: Canvas,
    iw: number,
    ih: number,
): Promise<Blob | null> {
    return new Promise((resolve) => {
        try {
            const out = document.createElement("canvas");
            out.width = iw;
            out.height = ih;
            const ctx = out.getContext("2d");
            if (!ctx) {
                resolve(null);
                return;
            }
            const mapEl = mapCanvas.lowerCanvasEl ?? mapCanvas.getElement();
            const annEl = annotationCanvas.lowerCanvasEl ?? annotationCanvas.getElement();
            ctx.drawImage(mapEl, 0, 0, mapEl.width, mapEl.height, 0, 0, iw, ih);
            ctx.drawImage(annEl, 0, 0, annEl.width, annEl.height, 0, 0, iw, ih);
            out.toBlob((b) => resolve(b), "image/png");
        } catch {
            resolve(null);
        }
    });
}
